import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { useForm } from "react-hook-form";
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";
import { contactUsPublic } from "../services/ContactPublicService";
import { useAxiosInstance } from "../services/Interceptor";
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "./LoadingSpinner";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-regular-svg-icons";

const CONTACT_EMAIL = "trackitverified@gmail.com";
const COPY_TOOLTIP_DURATION_MS = 2000;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const emailCopyTimeoutRef = useRef(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const apiInstance = useAxiosInstance();

  useEffect(() => {
    loadCaptchaEnginge(6);
  }, []);

  useEffect(() => {
    return () => {
      if (emailCopyTimeoutRef.current) {
        clearTimeout(emailCopyTimeoutRef.current);
      }
    };
  }, []);

  const submitForm = async (data) => {
    setLoading(true);
    const { captcha, ...contactData } = data;

    if (validateCaptcha(captcha)) {
      try {
        await contactUsPublic(apiInstance, contactData);
        toast.success("Message sent successfully!", {
          position: "top-center",
        });
      } catch {
        toast.error("Error sending message", {
          position: "top-center",
        });
      } finally {
        setLoading(false);
        reset();
      }
    } else {
      setLoading(false);
      toast.error("Captcha does not match. Please try again.", {
        position: "top-center",
      });
    }
  };

  const handleCopyEmail = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast.error("Clipboard is not available on this browser.", {
        position: "top-center",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setEmailCopied(true);

      if (emailCopyTimeoutRef.current) {
        clearTimeout(emailCopyTimeoutRef.current);
      }

      emailCopyTimeoutRef.current = setTimeout(() => {
        setEmailCopied(false);
        emailCopyTimeoutRef.current = null;
      }, COPY_TOOLTIP_DURATION_MS);
    } catch {
      toast.error("Unable to copy email address. Please try again.", {
        position: "top-center",
      });
    }
  };

  return (
    <>
      <Nav />
      <div className="text-center w-full max-w-7xl mx-auto px-4">
        <h2 className="text-white font-black italic text-2xl lg:text-4xl mb-0 lg:mb-1 pt-16 uppercase">
          {" "}
          Get in touch with Trackit Verified
        </h2>
        <p className=" text-gray-300 text-sm md:text-base pt-2 ">
          Our leadership team is here to help. Reach out directly or submit the
          form below.
        </p>
      </div>
      <div className="w-full max-w-7xl mx-auto my-4 lg:my-16 ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20">
          <div className="pt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 max-w-sm mx-auto">
                <img
                  className="rounded-xl object-cover w-full h-56 sm:h-60"
                  alt="Dan Higginson"
                  src="/DanHigginson.png"
                />
                <div className="mt-5 text-center md:text-left">
                  <h3 className="text-lg text-white font-semibold">
                    Dan Higginson
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">CEO</p>
                  <p className="mt-1 text-sm text-white">(801) 376-1575</p>
                </div>
              </div>

              <div className="p-4 max-w-sm mx-auto">
                <img
                  className="rounded-xl object-cover w-full h-56 sm:h-60"
                  alt="Porter Gustin"
                  src="/PorterGustin.png"
                />
                <div className="mt-5 text-center md:text-left">
                  <h3 className="text-lg text-white font-semibold">
                    Porter Gustin
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Founder, COO, NFL Veteran
                  </p>
                  <p className="mt-1 text-sm text-white">(208) 901-9484</p>
                </div>
              </div>
            </div>

            <p className="mt-8 text-white text-base text-center md:text-left whitespace-break-spaces px-5 lg:px-2">
              Reach out to our team at{" "}
              <span className="relative inline-flex items-center">
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-red-400 underline hover:text-red-600"
                >
                  {CONTACT_EMAIL}
                </a>
                <span className="relative ml-1 inline-flex">
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-300 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    aria-label={`Copy ${CONTACT_EMAIL} to clipboard`}
                  >
                    <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
                  </button>
                  {emailCopied && (
                    <span
                      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-red-50 px-2 py-1 text-xs font-normal text-black shadow"
                      role="status"
                      aria-live="polite"
                    >
                      Copied!
                    </span>
                  )}
                </span>
              </span>{" "}
              or fill out the form to have a member reach out to you.
            </p>
          </div>

          <div className="grid grid-flow-row auto-rows-max">
            <form className="" onSubmit={handleSubmit(submitForm)}>
              <div className="col-start-2 px-0 sm:px-4">
                <p className="text-white uppercase text-2xl text-left font-black italic">
                  Contact Us
                </p>
                <div className="form-field mt-4">
                  <label htmlFor="first">First Name</label>
                  <input
                    id="first"
                    type="text"
                    name="firstName"
                    aria-invalid={errors.firstName ? "true" : "false"}
                    {...register("firstName", { required: true })}
                    placeholder="First name..."
                    className="bg-transparent border border-gray-100 text-white"
                  ></input>
                </div>
                {errors.firstName && (
                  <p className="text-red-500">First name is required</p>
                )}
                <div className="form-field mt-4">
                  <label htmlFor="last">Last Name</label>
                  <input
                    id="last"
                    type="text"
                    name="lastName"
                    aria-invalid={errors.lastName ? "true" : "false"}
                    {...register("lastName", { required: true })}
                    placeholder="Last name"
                    className="bg-transparent border border-gray-100 text-white"
                  ></input>
                </div>
                {errors.lastName && (
                  <p className="text-red-500">Last name is required</p>
                )}
                <div className="form-field mt-4">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    aria-invalid={errors.email ? "true" : "false"}
                    name="email"
                    {...register("email", {
                      required: true,
                      pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
                    })}
                    className="bg-transparent border border-gray-100 text-white"
                    placeholder="Email..."
                  ></input>
                </div>
                {errors.email && (
                  <p className="text-red-500">Email is required</p>
                )}
                <div className="form-field mt-2">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    aria-invalid={errors.message ? "true" : "false"}
                    {...register("message", { required: true })}
                    placeholder="Message..."
                    className="bg-transparent border border-gray-100 placeholder-transparent h-12 text-white"
                  ></textarea>
                </div>
                {errors.message && (
                  <p className="text-red-500">Message is required</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                  <div className="">
                    <label className="sr-only" htmlFor="user_captcha_input">
                      Captcha
                    </label>
                    <input
                      id="user_captcha_input"
                      name="captcha"
                      aria-invalid={errors.captcha ? "true" : "false"}
                      {...register("captcha", { required: true })}
                      placeholder="Enter captcha "
                      className="border p-4 w-full rounded-md bg-transparent text-white"
                    />
                    {errors.captcha && (
                      <p className="mt-2 text-red-500">Captcha is required</p>
                    )}
                  </div>
                  <div className=" bg-white rounded-md px-1">
                    {" "}
                    <div className="mx-auto text-center">
                      <LoadCanvasTemplate reloadColor="black" />
                    </div>
                  </div>
                </div>

                <div className="form-field mt-4">
                  {loading && <LoadingSpinner />}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-red-500 p-3 mb-2 mt-2 w-full text-white uppercase rounded-full text-center hover:bg-red-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? "Sending..." : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className=" w-full max-w-7xl mx-auto border-t border-gray-500 mt-4">
        <Footer />
      </div>
    </>
  );
}
